import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useStore from "../../../../context/useStore";
import dataService from "../../../../helpers/dataService";
import tools from "../../../../helpers/tools";
import SaveBtn from "../../../common/SaveBtn";

const bootstrapToHex = {
  success: { bg: "#f0fdf4", color: "#16a34a", border: "#86efac" },
  danger: { bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" },
  warning: { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
  dark: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
  info: { bg: "#eff6ff", color: "#2563eb", border: "#93c5fd" },
  primary: { bg: "#eff6ff", color: "#2563eb", border: "#93c5fd" },
  secondary: { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" },
};

const getStatusStyle = (bsColor) =>
  bootstrapToHex[bsColor] || bootstrapToHex.dark;

const Jury = ({ ep, myjury, allMembers, sectors, fetchJuryDatas }) => {
  const [state, dispatch] = useStore();
  const constants = state.constants.items;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [jury, setJury] = useState(myjury);
  const [orgas, setOrgas] = useState([]);

  useEffect(() => {
    getOrgas();
  }, []);

  const addMember = (member) => {
    setJury((jury) => [...jury, { userManager: member }]);
  };
  const removeMember = (member) => {
    setJury((jury) => jury.filter((j) => j.userManager.id != member.id));
  };

  const getOrgas = () => {
    dataService.get(`organizations`, setOrgas);
  };

  const save = () => {
    var saveObj = jury.map((j) => ({ userManager: j.userManager.id }));

    setIsSaving(true);
    dataService.patch(
      `eps/${ep.id}/jury`,
      saveObj,
      (datas) => {
        toast.success("Jury enregistré avec succès");
        fetchJuryDatas();
      },
      setErrors,
      () => setIsSaving(false),
    );
  };

  const getInvitationStatus = (statusId) => {
    return constants.INVITATION_STATUS.find((inv) => inv.id == statusId);
  };

  return (
    <div>
      {/* Status counters */}
      <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
        {constants.INVITATION_STATUS.map((inv) => {
          const count = jury.filter((j) => j.invitationStatus == inv.id).length;
          const style = getStatusStyle(inv.color);
          return (
            <div
              key={inv.id}
              className="d-flex align-items-center px-2 py-1 rounded"
              style={{
                background: style.bg,
                border: `1px solid ${style.border}`,
                gap: 6,
              }}
            >
              <i
                className={`fa fa-${inv.icon}`}
                style={{ fontSize: 11, color: style.color }}
              />
              <span
                style={{ fontSize: 13, fontWeight: 700, color: style.color }}
              >
                {count}
              </span>
              <span style={{ fontSize: 12, color: style.color, opacity: 0.85 }}>
                {inv.name}
              </span>
            </div>
          );
        })}
        <div
          className="d-flex align-items-center px-2 py-1 rounded"
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            gap: 6,
          }}
        >
          <i
            className="fa fa-users"
            style={{ fontSize: 11, color: "#64748b" }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
            {jury.length}
          </span>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            membre{jury.length > 1 ? "s" : ""} au total
          </span>
        </div>
      </div>

      {/* Jury members list */}
      {jury.length === 0 ? (
        <div
          className="text-center p-4 rounded"
          style={{ background: "#f8fafc", border: "1px dashed #cbd5e1" }}
        >
          <i
            className="fa fa-users mb-2"
            style={{ fontSize: 24, color: "#94a3b8", display: "block" }}
          />
          <span style={{ fontSize: 14, color: "#64748b" }}>
            Aucun membre dans le jury pour le moment.
          </span>
        </div>
      ) : (
        <div className="d-flex flex-column" style={{ gap: 8 }}>
          {jury.map((j) => {
            var user = j.userManager;
            const invStatus = getInvitationStatus(j.invitationStatus);
            const invStyle = invStatus ? getStatusStyle(invStatus.color) : null;
            const isOutOfSector = !allMembers.userManagers?.find(
              (u) => u == user.id,
            );

            return (
              <div
                key={`members${user.id}`}
                className="d-flex align-items-center rounded"
                style={{
                  background: "#fff",
                  border: isOutOfSector
                    ? "1px solid #fca5a5"
                    : "1px solid #e2e8f0",
                  padding: "10px 14px",
                  gap: 12,
                  transition: "all 0.15s ease",
                }}
              >
                {/* Avatar */}
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: invStatus
                      ? invStyle.bg
                      : "linear-gradient(135deg, #e2e8f0, #cbd5e1)",
                    border: invStatus
                      ? `2px solid ${invStyle.border}`
                      : "2px solid #e2e8f0",
                    fontSize: 14,
                    fontWeight: 700,
                    color: invStatus ? invStyle.color : "#64748b",
                  }}
                >
                  {tools
                    .getFullname(user)
                    .split(" ")
                    .filter(
                      (w) =>
                        !["mr", "mme", "mlle", "m.", "m"].includes(
                          w.toLowerCase(),
                        ),
                    )
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    {isOutOfSector && (
                      <i
                        className="fa fa-exclamation-circle cursor-pointer"
                        title="L'utilisateur ne fait plus partie de ce secteur"
                        onClick={() =>
                          alert(
                            "L'utilisateur ne fait plus partie de ce secteur.",
                          )
                        }
                        style={{ fontSize: 12, color: "#dc2626" }}
                      />
                    )}
                    <span
                      className="text-truncate"
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#1e293b",
                      }}
                    >
                      {tools.getFullname(user)}
                    </span>
                  </div>
                  {user.phone && (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      {user.phone}
                    </div>
                  )}
                  {orgas.length > 0 && user.organizations?.length > 0 && (
                    <div className="d-flex flex-wrap mt-1" style={{ gap: 4 }}>
                      {user.organizations.map((or) => {
                        const org = orgas.find((org) => org.id == or);
                        return (
                          <span
                            key={or}
                            className="px-2 py-0 rounded"
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                              background: "#f1f5f9",
                              color: "#64748b",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            {org?.name || or + " non trouvé"}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Invitation status */}
                <div
                  className="d-flex flex-column align-items-end flex-shrink-0"
                  style={{ minWidth: 140 }}
                >
                  {j.invitationSentAt ? (
                    <>
                      <div
                        className="d-flex align-items-center px-2 py-1 rounded"
                        style={{
                          background: invStyle?.bg || "#f1f5f9",
                          border: `1px solid ${invStyle?.border || "#e2e8f0"}`,
                          gap: 5,
                        }}
                      >
                        {invStatus && (
                          <i
                            className={`fa fa-${invStatus.icon}`}
                            style={{
                              fontSize: 10,
                              color: invStyle?.color || "#64748b",
                            }}
                          />
                        )}
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: invStyle?.color || "#64748b",
                          }}
                        >
                          {invStatus?.name || "—"}
                        </span>
                        {j.invitationStatusUpdatedAt && (
                          <span style={{ fontSize: 10, color: "#94a3b8" }}>
                            répondu le{" "}
                            {DateTime.fromISO(
                              j.invitationStatusUpdatedAt,
                            ).toFormat("dd/MM/yyyy")}
                          </span>
                        )}
                      </div>
                      <div
                        className="d-flex align-items-center mt-1"
                        style={{ gap: 4 }}
                      >
                        <i
                          className="fa fa-paper-plane"
                          style={{ fontSize: 9, color: "#16a34a" }}
                        />
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>
                          invité•e le{" "}
                          {DateTime.fromISO(j.invitationSentAt).toFormat(
                            "dd/MM/yyyy",
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div
                      className="d-flex align-items-center px-2 py-1 rounded"
                      style={{
                        background: "#f8fafc",
                        border: "1px dashed #cbd5e1",
                        gap: 5,
                      }}
                    >
                      <i
                        className="fa fa-bell-slash"
                        style={{ fontSize: 10, color: "#94a3b8" }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          fontStyle: "italic",
                        }}
                      >
                        pas encore invité•e
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* <div className="d-flex w-100 justify-content-center mt-3">
        <SaveBtn save={save} isSaving={isSaving} />
      </div> */}
    </div>
  );
};

export default Jury;
